import _, { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Button } from 'src/@vymo/ui/atoms';
import Loader from 'src/@vymo/ui/atoms/loader';
import Text from 'src/@vymo/ui/atoms/text';
import CodeEditor from 'src/@vymo/ui/blocks/codeEditor';
import Modal, { Body, Header } from 'src/@vymo/ui/blocks/modal';
import { MimeTypes } from 'src/@vymo/ui/molecules/documentUploader/constants';
import ImageTool from 'src/@vymo/ui/molecules/documentUploader/imageTool';
import { getFileType } from 'src/@vymo/ui/molecules/documentUploader/queries';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import classNames from 'classnames';
import { ReactComponent as DownloadIcon } from 'src/assets/icons/document-download.svg';
import { isMobile } from 'src/workspace/utils';
import {
  getDecodedString,
  getHeicPreviewUrl,
  getIsFilePreviewSupported,
} from './queries';
import styles from './index.module.scss';

function LoadingRenderer() {
  return <Loader visible />;
}

function DocPreview({
  document,
  modal = true,
  open = false,
  onClose = _.noop,
  zoom = false,
  className = '',
}) {
  const [textContent, setTextContent] = useState('');
  const [fileReadError, setFileReadError] = useState('');
  const [heicPreviewUrl, setHeicPreviewUrl] = useState('');

  const fileType = getFileType(document?.file) as MimeTypes;

  useEffect(() => {
    if (
      document?.file &&
      [MimeTypes.TEXT, MimeTypes.JSON, MimeTypes.MD].includes(fileType)
    ) {
      const reader = new FileReader();
      reader.onload = (e: any) => setTextContent(e.target.result);
      reader.onerror = () => setFileReadError('Failed to load file content');
      reader.readAsText(document?.file);
    }
  }, [document?.file, fileType]);

  useEffect(() => {
    if (document?.file && [MimeTypes.HEIC, MimeTypes.HEIF].includes(fileType)) {
      getHeicPreviewUrl(document?.file)
        .then((url) => setHeicPreviewUrl(url))
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to generate HEIC preview:', error);
        });
    }
  }, [document?.file, fileType]);

  const downloadFile = useCallback(() => {
    if (!document?.file) return;
    const previewUrl = URL.createObjectURL(document.file);

    if (!isMobile()) {
      onClose();
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
    } else {
      const link = window.document.createElement('a');
      link.href = previewUrl;
      link.download = document.file.name || 'download';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(previewUrl);
      onClose();
    }
  }, [document, onClose]);

  const isFilePreviewSupported = useMemo(
    () => getIsFilePreviewSupported(document?.file),
    [document?.file],
  );

  // eslint-disable-next-line complexity
  const getDocPreviewRender = useCallback(() => {
    let json = '';
    const fileSizeInMB = (document?.file?.size ?? 0) / (1024 * 1024);
    const isLargeCSV = fileType === MimeTypes.CSV && fileSizeInMB > 1;
    const isLargePDF = fileType === MimeTypes.PDF && fileSizeInMB > 2;

    if (isLargeCSV || isLargePDF) {
      return (
        <div className={styles.previewModal__fallback}>
          <Button type="outlined" onClick={downloadFile}>
            Download {fileType === MimeTypes.PDF ? 'PDF' : 'CSV'}
          </Button>
          <div className={styles.previewModal__fallback__message}>
            <div>
              This {fileType === MimeTypes.PDF ? 'PDF' : 'CSV'} file is{' '}
              {fileSizeInMB.toFixed(2)} MB and may cause the browser to slow
              down.
            </div>
            <div>We recommend downloading the file.</div>
          </div>
        </div>
      );
    }

    switch (fileType) {
      case MimeTypes.DOC:
      case MimeTypes.DOCX:
      case MimeTypes.PPT:
      case MimeTypes.PPTX:
      case MimeTypes.XLS:
      case MimeTypes.XLSX:
        return (
          <Button type="outlined" onClick={downloadFile}>
            Download File
          </Button>
        );

      case MimeTypes.HEIC:
      case MimeTypes.HEIF: {
        if (modal && open) {
          return (
            <ImageTool
              imageData={[{ uri: heicPreviewUrl, file: document.file }]}
              modal={false}
              disableCrop
              onChange={onClose}
              classNames={styles.previewModal__docViewer}
              canZoom={zoom}
            />
          );
        }
        return (
          <img
            className={styles.previewModal__docViewer}
            src={heicPreviewUrl}
            alt=""
          />
        );
      }

      case MimeTypes.JPEG:
      case MimeTypes.JPG:
      case MimeTypes.PNG:
      case MimeTypes.SVG: {
        const previewUrl = URL.createObjectURL(document?.file);
        if (modal && open) {
          return (
            <ImageTool
              imageData={[{ uri: previewUrl, file: document.file }]}
              modal={false}
              disableCrop
              onChange={onClose}
              classNames={styles.previewModal__docViewer}
              canZoom={zoom}
            />
          );
        }
        return (
          <img
            className={styles.previewModal__docViewer}
            src={previewUrl}
            alt=""
          />
        );
      }

      case MimeTypes.TEXT:
        return (
          <div className={styles.previewModal__body__fileContent}>
            <pre>{fileReadError || textContent}</pre>
          </div>
        );

      case MimeTypes.JSON:
        try {
          json = JSON.stringify(JSON.parse(textContent), null, 2);
        } catch (e) {
          json = 'Invalid JSON format';
        }
        return (
          <div className={styles.previewModal__body__fileContent}>
            <CodeEditor
              theme="vs-light"
              language="json"
              value={fileReadError || json}
              width="100%"
              height="100%"
            />
          </div>
        );

      case MimeTypes.MD:
        return (
          <div className={styles.previewModal__body__fileContent}>
            {fileReadError ? (
              <pre>{fileReadError}</pre>
            ) : (
              <ReactMarkdown>{textContent}</ReactMarkdown>
            )}
          </div>
        );

      default:
        return (
          <DocViewer
            documents={[document]}
            pluginRenderers={DocViewerRenderers}
            className={styles.previewModal__docViewer}
            config={{
              header: { disableFileName: true },
              pdfVerticalScrollByDefault: true,
              loadingRenderer: {
                overrideComponent: LoadingRenderer,
                showLoadingTimeout: 0,
              },
            }}
            theme={{
              textPrimary: 'var(--text-default)',
              textSecondary: 'var(--text-subtle)',
            }}
          />
        );
    }
  }, [
    document,
    fileType,
    downloadFile,
    heicPreviewUrl,
    onClose,
    fileReadError,
    textContent,
    modal,
    open,
    zoom,
  ]);

  if (!isEmpty(document)) {
    if (modal && open) {
      if (isFilePreviewSupported) {
        return (
          <Modal
            showCloseButton
            open
            onClose={onClose}
            closeOnEscape
            classNames={classNames(className, styles.previewModal)}
            data-test-id="document-preview-modal"
          >
            <Header className={styles.previewModal__header}>
              <div className={styles.previewModal__header__content}>
                <Text
                  classNames={styles.previewModal__header__content__fileName}
                >
                  {getDecodedString(document?.file?.name || document?.fileName)}
                </Text>
                <div className={styles.previewModal__header__content__actions}>
                  <a
                    href={document?.uri}
                    download={document?.file?.name || document?.fileName}
                  >
                    <Button
                      className={
                        styles.previewModal__header__content__downloadButton
                      }
                      type="outlined"
                      iconProps={{
                        icon: <DownloadIcon />,
                        iconPosition: 'left',
                      }}
                    >
                      {!isMobile() && 'Download'}
                    </Button>
                  </a>
                </div>
              </div>
            </Header>
            <Body className={styles.previewModal__body}>
              {getDocPreviewRender()}
            </Body>
          </Modal>
        );
      }
      downloadFile();
      return null;
    }

    if (!modal) {
      return getDocPreviewRender();
    }
  }

  return null;
}

export default React.memo(DocPreview);
